'use client'

/**
 * Policies — every admin read and write, as a TanStack Query hook.
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform; the
 * Policies module puts an organisation's own policy PDFs in front of its own staff, who
 * read, tick, answer and acknowledge, and keeps each acknowledgment as evidence.
 *
 * Components call THESE hooks and never lib/policies/api.ts. The employee portal and the
 * signed link have their own file: portal-hooks.ts.
 *
 * Conventions (hfv3-frontend-builder; apps/web/CLAUDE.md rule 1):
 *  - A read is gated on a concrete workspace (`useWorkspaceScopedEnabled`) and, where it
 *    takes ids, on those ids. Keys come from lib/policies/query-keys.ts and carry the
 *    resource and the request only (no mode, no workspace id: WorkspaceCacheGuard clears
 *    the cache on a workspace switch).
 *  - Lists and the objects inside them are normalised here, once, so a screen never guards
 *    against a missing array or count. Nullable VALUES stay null (the contract's "absent is
 *    null"); only collections are filled in.
 *  - A WRITE TAKES ITS IDS IN ITS VARIABLES (`mutate({ policyId, versionId, … })`), not in
 *    the hook's arguments: the library's row buttons, the policy page and the editor all
 *    call the same hook for different policies. Every write refreshes what it changed
 *    (the invalidation map in query-keys.ts) and the screen words a refusal with
 *    `policyErrorMessage(t, error)` (lib/policies/errors.ts) — never `error.message`.
 *  - A paid or sending action (AI run, reminder, assignment) is a mutation, never a query.
 *
 * Endpoint and key of each hook are listed beside it. FROZEN after the contract phase.
 */
import { useCallback } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { aiAllowanceKeys } from '@/hooks/ai/use-ai-credits'
import { ApiClientError, fetchApi } from '@/lib/api/client'
import { useWorkspaceScopedEnabled } from '@/lib/api/use-workspace-scoped-enabled'
import { useTranslation } from '@/lib/i18n'
import {
  downloadPolicyEvidencePdf,
  downloadPolicyReceiptPdf,
  exportPolicyLibraryCsv,
  exportPolicyPeopleCsv,
  fetchPolicyPdfBytes,
  fetchPolicyVersionFile,
  policyApi,
  policyPaths,
  qsOf,
  savePolicyBlob,
  uploadPolicyFile,
} from './api'
import type { PolicyPdfBytes, PolicyPdfSource } from './doors'
import { isPolicyKey, policyKeys } from './query-keys'
import {
  POLICY_EXEMPTION_STATUSES,
  POLICY_PEOPLE_STATUS_FILTERS,
  type ArchivePolicyBody,
  type CreatePolicyTestRunBody,
  type DeclinePolicyExemptionBody,
  type DecidePolicyExemptionBody,
  type MarkPolicyReviewedBody,
  type PolicyArchiveResult,
  type PolicyAsOf,
  type PolicyAsOfQuery,
  type PolicyAskAgainPreview,
  type PolicyAssignmentCancelResult,
  type PolicyChosenChangeType,
  type PolicyComplianceChangeResult,
  type PolicyCompliancePreview,
  type PolicyDetailsChanged,
  type PolicyEvidenceIntegrity,
  type PolicyExemptionDecisionResult,
  type PolicyExemptionPage,
  type PolicyExemptionRequestResult,
  type PolicyExemptionsQuery,
  type PolicyReviewRow,
  type PolicySendNowResult,
  type PolicyTestRunRow,
  type PolicyVersionCreated,
  type PreviewAskPolicyAgainBody,
  type RequestPolicyExemptionsBody,
  type UpdatePolicyDetailsBody,
  type AskPolicyAgainBody,
  type AssignPolicyBody,
  type ChangePolicyDueDateBody,
  type ExtendPolicyBody,
  type PolicyActivityLogPage,
  type PolicyActivityLogQuery,
  type PolicyActivitySeriesQuery,
  type PolicyAiDetailsResult,
  type PolicyAiSuggestion,
  type PolicyAiSuggestionsQuery,
  type PolicyArchivedPage,
  type PolicyArchivedQuery,
  type PolicyDraftFilter,
  type PolicyDraftPage,
  type PolicyDraftsQuery,
  type PolicyInbox,
  type PolicyLibraryPage,
  type PolicyLibraryQuery,
  type PolicyLibrarySection,
  type PolicyPeoplePage,
  type PolicyPeopleQuery,
  type PolicyPeopleStatusFilter,
  type PolicyPage,
  type PolicyVersionReview,
  type PolicyVersionDetail,
  type PolicyVersionRow,
  type RemindPolicyBody,
  type RemovePolicyRecipientBody,
  type RequestPolicyChangesBody,
  type RunPolicyAiBody,
  type SubmitPolicyVersionBody,
  type UpdatePolicyBody,
  type UpdatePolicyRemindersBody,
  type UpdatePolicyVersionBody,
} from './types'

// ── Normalisers (apps/web/CLAUDE.md rule 1: screens get complete data) ───────

/**
 * The list envelope with every field present. A page that names no zone gets '' (never 'UTC':
 * the workspace zone is the tenant zone, CLAUDE.md rule 9), which TimesInNote draws as nothing.
 */
function normalisePage<T, P extends PolicyPage<T>>(page: P | undefined, limit: number): P {
  const total = typeof page?.total === 'number' ? page.total : 0
  const pageLimit = typeof page?.limit === 'number' && page.limit > 0 ? page.limit : limit
  return {
    ...(page as P),
    data: Array.isArray(page?.data) ? page.data : [],
    total,
    page: typeof page?.page === 'number' ? page.page : 1,
    limit: pageLimit,
    totalPages: typeof page?.totalPages === 'number' ? page.totalPages : Math.max(1, Math.ceil(total / pageLimit)),
    unfilteredTotal: typeof page?.unfilteredTotal === 'number' ? page.unfilteredTotal : total,
    timeZone: page?.timeZone ?? '',
  } as P
}

/** Zero per status, so a facet or chip never reads a missing count. */
function zeroCounts<K extends string>(keys: readonly K[]): Record<K, number> {
  return Object.fromEntries(keys.map((k) => [k, 0])) as Record<K, number>
}

const DRAFT_FILTERS: readonly PolicyDraftFilter[] = ['All', 'Draft', 'Awaiting Approval', 'Changes Requested']

function normaliseDrafts(page: PolicyDraftPage | undefined, limit: number): PolicyDraftPage {
  const base = normalisePage<PolicyDraftPage['data'][number], PolicyDraftPage>(page, limit)
  return { ...base, statusCounts: { ...zeroCounts(DRAFT_FILTERS), ...(page?.statusCounts ?? {}) } }
}

function normalisePeople(page: PolicyPeoplePage | undefined, limit: number): PolicyPeoplePage {
  const base = normalisePage<PolicyPeoplePage['data'][number], PolicyPeoplePage>(page, limit)
  return {
    ...base,
    statusCounts: { ...zeroCounts<PolicyPeopleStatusFilter>(POLICY_PEOPLE_STATUS_FILTERS), ...(page?.statusCounts ?? {}) },
    departments: Array.isArray(page?.departments) ? page.departments : [],
  }
}

function normaliseInbox(inbox: PolicyInbox | undefined): PolicyInbox {
  return {
    waitingForMe: Array.isArray(inbox?.waitingForMe) ? inbox.waitingForMe : [],
    sentBackToMe: Array.isArray(inbox?.sentBackToMe) ? inbox.sentBackToMe : [],
    exemptionsToDecide: typeof inbox?.exemptionsToDecide === 'number' ? inbox.exemptionsToDecide : 0,
    counts: { published: 0, drafts: 0, archived: 0, ...(inbox?.counts ?? {}) },
  }
}

/** A version with every collection present (a screen maps keyPoints, questions and pages without a guard). */
export function normaliseVersion<V extends PolicyVersionDetail>(version: V): V {
  return {
    ...version,
    pages: Array.isArray(version.pages) ? version.pages : [],
    changedPages: Array.isArray(version.changedPages) ? version.changedPages : [],
    prefilled: Array.isArray(version.prefilled) ? version.prefilled : [],
    keyPoints: Array.isArray(version.keyPoints) ? version.keyPoints : [],
    questions: Array.isArray(version.questions) ? version.questions : [],
    issues: Array.isArray(version.issues) ? version.issues : [],
  }
}

const isNotFound = (err: unknown) => err instanceof ApiClientError && err.statusCode === 404
const retryUnlessNotFound = (count: number, err: unknown) => !isNotFound(err) && count < 2

// ── Invalidation ────────────────────────────────────────────────────────────

/** Everything cached about one policy (its page, versions, people, figures, reports, assignments). */
function invalidatePolicy(qc: QueryClient, policyId: string) {
  return qc.invalidateQueries({ predicate: (q) => isPolicyKey(q.queryKey, policyId) })
}

/** The lists, the inbox, the sidebar badge, the picker options: what any write to a policy can move. */
function invalidateLibrary(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: policyKeys.library.all(), predicate: (q) => !isDetailKey(q.queryKey) })
}

/** library.detail(id) keys are per-policy: isPolicyKey owns them. */
const isDetailKey = (queryKey: readonly unknown[]) => queryKey[2] === 'detail'

/** The dashboard and the "waiting for me" counts: organisation-wide figures a policy write can move. */
function invalidateOrganisation(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: policyKeys.dashboards() })
  void qc.invalidateQueries({ queryKey: policyKeys.options.all() })
}

/** A version's page list, page text, issues, review and compare: everything derived from its file or content. */
function invalidateVersionDerived(qc: QueryClient, policyId: string, versionId: string) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey
      return isPolicyKey(key, policyId) && key[1] === 'versions' && key.includes(versionId) && key[3] !== 'detail'
    },
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// Library and navigation
// ═════════════════════════════════════════════════════════════════════════════

/** GET /policies/approvals/count · policyKeys.library.approvalsCount() — the sidebar badge (holders of the approve right pass `enabled`). */
export function usePolicyApprovalsCount(enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.library.approvalsCount(),
    queryFn: () => policyApi.approvalsCount(),
    enabled: scoped && enabled,
    staleTime: 30_000,
    retry: false,
  })
}

/** GET /policies/library/inbox · policyKeys.library.inbox() — "waiting for me", "sent back to me", tab counts. */
export function usePolicyLibraryInbox() {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.library.inbox(),
    queryFn: () => policyApi.inbox(),
    select: normaliseInbox,
    enabled: scoped,
  })
}

/** GET /policies/library?section=published · policyKeys.library.list('published', qs). */
export function usePublishedPolicies(query: PolicyLibraryQuery = {}) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.library.list('published', qs),
    queryFn: () => policyApi.listPublished(query),
    select: (page: PolicyLibraryPage) => normalisePage<PolicyLibraryPage['data'][number], PolicyLibraryPage>(page, query.limit ?? 25),
    placeholderData: keepPreviousData,
    enabled: scoped,
  })
}

/** GET /policies/library?section=drafts · policyKeys.library.list('drafts', qs). */
export function usePolicyDrafts(query: PolicyDraftsQuery = {}) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.library.list('drafts', qs),
    queryFn: () => policyApi.listDrafts(query),
    select: (page: PolicyDraftPage) => normaliseDrafts(page, query.limit ?? 25),
    placeholderData: keepPreviousData,
    enabled: scoped,
  })
}

/** GET /policies/library?section=archived · policyKeys.library.list('archived', qs). */
export function useArchivedPolicies(query: PolicyArchivedQuery = {}) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.library.list('archived', qs),
    queryFn: () => policyApi.listArchived(query),
    select: (page: PolicyArchivedPage) => normalisePage<PolicyArchivedPage['data'][number], PolicyArchivedPage>(page, query.limit ?? 25),
    placeholderData: keepPreviousData,
    enabled: scoped,
  })
}

/** GET /policies/library/:id · policyKeys.library.detail(id). A 404 is an answer, not a hiccup (no retry). */
export function usePolicy(policyId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.library.detail(policyId ?? ''),
    queryFn: () => policyApi.getPolicy(policyId as string),
    select: (detail) => ({ ...detail, rounds: Array.isArray(detail.rounds) ? detail.rounds : [] }),
    enabled: scoped && !!policyId,
    retry: retryUnlessNotFound,
  })
}

/** GET /policies/library/:id/archive/preview · policyKeys.library.archivePreview(id) — the archive dialog's numbers. */
export function usePolicyArchivePreview(policyId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.library.archivePreview(policyId ?? ''),
    queryFn: () => policyApi.archivePreview(policyId as string),
    enabled: scoped && enabled && !!policyId,
    gcTime: 0,
  })
}

/** GET /policies/roles · policyKeys.roles() — who may submit, approve, assign, see evidence. */
export function usePolicyRoles(enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.roles(),
    queryFn: () => policyApi.roles(),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled,
  })
}

/** GET /policies/approvers?excludeSelf= · policyKeys.approvers(excludeSelf) — the submit dialog's picker (empty = `policy_no_approver`). */
export function usePolicyApprovers(excludeSelf = true, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.approvers(excludeSelf),
    queryFn: () => policyApi.approvers(excludeSelf),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled,
  })
}

/** GET /policies/options/remedial · policyKeys.options.remedial() — published policies a simulation or workflow may require. */
export function usePolicyRemedialOptions(enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.options.remedial(),
    queryFn: () => policyApi.remedialOptions(),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled,
  })
}

/** What a screen may say about one Policies email: its switch in Settings -> Notifications -> Emails. */
export type PolicyEmailState = 'on' | 'off' | 'unknown'

/**
 * GET /policies/options/emails · policyKeys.options.emails() — which Policies emails are switched ON, for the dialogs that
 * promise mail (Assign, Ask again, Publish, Extend, Remind, Change due date). They ask while they are OPEN (`enabled`), and
 * every time: the answer is never kept (`staleTime` and `gcTime` 0), because the switch lives on another page.
 *
 * `stateOf(key)` is 'unknown' while the answer is loading or being read again, when it failed, and for a key it does not
 * carry: a screen then words the line so that it PROMISES NOTHING (the action itself still asks the switch, and its
 * answer tells the truth afterwards).
 */
export function usePolicyEmailSwitches(enabled = true): { stateOf: (key: string) => PolicyEmailState } {
  const scoped = useWorkspaceScopedEnabled()
  const query = useQuery({
    queryKey: policyKeys.options.emails(),
    queryFn: () => policyApi.emailSwitches(),
    select: (answer): Record<string, boolean> => (answer && typeof answer.emails === 'object' && answer.emails !== null ? answer.emails : {}),
    staleTime: 0,
    gcTime: 0,
    enabled: scoped && enabled,
  })
  const settled = query.isSuccess && !query.isFetching
  const emails = settled ? query.data : null
  return {
    stateOf: (key) => {
      const on = emails?.[key]
      return on === true ? 'on' : on === false ? 'off' : 'unknown'
    },
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// Versions, pages, review
// ═════════════════════════════════════════════════════════════════════════════

/** GET /policies/library/:id/versions · policyKeys.versions.list(id) — the Versions tab. */
export function usePolicyVersions(policyId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.list(policyId ?? ''),
    queryFn: () => policyApi.listVersions(policyId as string),
    select: (rows: PolicyVersionRow[]) => (Array.isArray(rows) ? rows.map((r) => ({ ...r, changedPages: Array.isArray(r.changedPages) ? r.changedPages : [] })) : []),
    enabled: scoped && !!policyId,
  })
}

/** GET /policies/library/:id/versions/:versionId · policyKeys.versions.detail(id, versionId) — the editor and the read-only content. */
export function usePolicyVersion(policyId: string | null | undefined, versionId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.detail(policyId ?? '', versionId ?? ''),
    queryFn: () => policyApi.getVersion(policyId as string, versionId as string),
    select: normaliseVersion,
    enabled: scoped && !!policyId && !!versionId,
    retry: retryUnlessNotFound,
  })
}

/** GET …/versions/:versionId/review · policyKeys.versions.review(id, versionId) — the version, the checks, the re-acknowledgment plan, the diff. */
export function usePolicyVersionReview(policyId: string | null | undefined, versionId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.review(policyId ?? '', versionId ?? ''),
    queryFn: () => policyApi.versionReview(policyId as string, versionId as string),
    select: (review: PolicyVersionReview) => ({
      ...normaliseVersion(review),
      checks: Array.isArray(review.checks) ? review.checks : [],
      diff: {
        ...review.diff,
        changedPages: Array.isArray(review.diff?.changedPages) ? review.diff.changedPages : [],
        keyPoints: Array.isArray(review.diff?.keyPoints) ? review.diff.keyPoints : [],
        questions: Array.isArray(review.diff?.questions) ? review.diff.questions : [],
      },
    }),
    enabled: scoped && !!policyId && !!versionId,
    retry: retryUnlessNotFound,
  })
}

/**
 * GET …/versions/:to/compare?with=:from · policyKeys.versions.compare(id, to, from) — what
 * version `to` changed relative to version `from` (the Compare screen's `?from=&to=`).
 */
export function usePolicyVersionCompare(policyId: string | null | undefined, from: string | null | undefined, to: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.compare(policyId ?? '', to ?? '', from ?? ''),
    queryFn: () => policyApi.versionCompare(policyId as string, to as string, from as string),
    select: (diff) => ({
      ...diff,
      changedPages: Array.isArray(diff.changedPages) ? diff.changedPages : [],
      keyPoints: Array.isArray(diff.keyPoints) ? diff.keyPoints : [],
      questions: Array.isArray(diff.questions) ? diff.questions : [],
    }),
    enabled: scoped && !!policyId && !!from && !!to && from !== to,
    retry: retryUnlessNotFound,
  })
}

/** GET …/versions/:versionId/pages · policyKeys.versions.pages(id, versionId) — the thumbnails (title, "Changed"). */
export function usePolicyVersionPages(policyId: string | null | undefined, versionId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.pages(policyId ?? '', versionId ?? ''),
    queryFn: () => policyApi.versionPages(policyId as string, versionId as string),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled && !!policyId && !!versionId,
  })
}

/** GET …/versions/:versionId/pages/:pageNo · policyKeys.versions.page(id, versionId, pageNo) — one page's extracted text (screen readers, previews). */
export function usePolicyVersionPage(
  policyId: string | null | undefined,
  versionId: string | null | undefined,
  pageNo: number | null | undefined,
  enabled = true,
) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.page(policyId ?? '', versionId ?? '', pageNo ?? 0),
    queryFn: () => policyApi.versionPage(policyId as string, versionId as string, pageNo as number),
    enabled: scoped && enabled && !!policyId && !!versionId && !!pageNo,
  })
}

/** GET …/versions/:versionId/issues · policyKeys.versions.issues(id, versionId) — the "things to fix" list (also inside the version detail). */
export function usePolicyVersionIssues(policyId: string | null | undefined, versionId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.versions.issues(policyId ?? '', versionId ?? ''),
    queryFn: () => policyApi.versionIssues(policyId as string, versionId as string),
    select: (result) => (Array.isArray(result?.issues) ? result.issues : []),
    enabled: scoped && enabled && !!policyId && !!versionId,
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// The policy page: figures, people, assignments, reports, activity
// ═════════════════════════════════════════════════════════════════════════════

/** GET /policies/library/:id/progress?round= · policyKeys.progress(id, round) — the stage strip's figures. `round` null = current. */
export function usePolicyProgress(policyId: string | null | undefined, round: number | null = null) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.progress(policyId ?? '', round),
    queryFn: () => policyApi.progress(policyId as string, round),
    enabled: scoped && !!policyId,
    placeholderData: keepPreviousData,
    retry: retryUnlessNotFound,
  })
}

/** GET /policies/library/:id/people · policyKeys.people.list(id, qs) — the Recipients tab (needs read:Recipient). */
export function usePolicyPeople(policyId: string | null | undefined, query: PolicyPeopleQuery = {}, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.people.list(policyId ?? '', qs),
    queryFn: () => policyApi.people(policyId as string, query),
    select: (page: PolicyPeoplePage) => normalisePeople(page, query.limit ?? 25),
    placeholderData: keepPreviousData,
    enabled: scoped && enabled && !!policyId,
  })
}

/** GET /policies/library/:id/people/:attestationId · policyKeys.people.person(id, attestationId) — one person's evidence record. */
export function usePolicyPerson(policyId: string | null | undefined, attestationId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.people.person(policyId ?? '', attestationId ?? ''),
    queryFn: () => policyApi.person(policyId as string, attestationId as string),
    select: (record) => ({ ...record, events: Array.isArray(record.events) ? record.events : [] }),
    enabled: scoped && !!policyId && !!attestationId,
    retry: retryUnlessNotFound,
  })
}

/** GET /policies/library/:id/assignments?round= · policyKeys.assignments.list(id, round) — the Settings tab's "Assignment 1, 2 …". */
export function usePolicyAssignments(policyId: string | null | undefined, round: number | null = null) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.assignments.list(policyId ?? '', round),
    queryFn: () => policyApi.assignments(policyId as string, round),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && !!policyId,
  })
}

/** GET /policies/library/:id/reminders · policyKeys.reminders(id) — the four switches of the current round. */
export function usePolicyReminders(policyId: string | null | undefined) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.reminders(policyId ?? ''),
    queryFn: () => policyApi.reminders(policyId as string),
    enabled: scoped && !!policyId,
  })
}

/**
 * POST /policies/library/:id/assignments/preview · policyKeys.assignments.preview(id, json) —
 * the live head-count while the picker changes. Pass null to hold the request; the caller
 * debounces `body` (≈ 400 ms) so typing does not fire a request per key.
 */
export function usePolicyAudiencePreview(policyId: string | null | undefined, body: AssignPolicyBody | null) {
  const scoped = useWorkspaceScopedEnabled()
  const key = body ? JSON.stringify(body) : ''
  return useQuery({
    queryKey: policyKeys.assignments.preview(policyId ?? '', key),
    queryFn: () => policyApi.previewAssignment(policyId as string, body as AssignPolicyBody),
    select: (preview) => ({
      ...preview,
      bulkPasteUnknown: Array.isArray(preview.bulkPasteUnknown) ? preview.bulkPasteUnknown : [],
      groupAlreadyAssigned: preview.groupAlreadyAssigned ?? {},
      recipientAssigned: preview.recipientAssigned ?? {},
      schedule: Array.isArray(preview.schedule) ? preview.schedule : [],
    }),
    enabled: scoped && !!policyId && !!body,
    placeholderData: keepPreviousData,
  })
}

/** GET /policies/library/:id/late-joiners · policyKeys.lateJoiners(id) — "N recipients aren't assigned this policy". */
export function usePolicyLateJoiners(policyId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.lateJoiners(policyId ?? ''),
    queryFn: () => policyApi.lateJoiners(policyId as string),
    select: (late) => ({
      ...late,
      sampleNames: Array.isArray(late.sampleNames) ? late.sampleNames : [],
      recipientIds: Array.isArray(late.recipientIds) ? late.recipientIds : [],
    }),
    enabled: scoped && enabled && !!policyId,
  })
}

/** GET /policies/library/:id/reports?round= · policyKeys.reports(id, round) — drop-off, most-missed questions, departments. */
export function usePolicyReports(policyId: string | null | undefined, round: number | null = null) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.reports(policyId ?? '', round),
    queryFn: () => policyApi.reports(policyId as string, round),
    select: (reports) => ({
      ...reports,
      stages: Array.isArray(reports.stages) ? reports.stages : [],
      mostMissed: Array.isArray(reports.mostMissed) ? reports.mostMissed : [],
      departments: Array.isArray(reports.departments) ? reports.departments : [],
    }),
    enabled: scoped && !!policyId,
    placeholderData: keepPreviousData,
  })
}

/** GET /policies/library/:id/activity/series · policyKeys.activity.series(id, qs) — the Activity chart. */
export function usePolicyActivitySeries(policyId: string | null | undefined, query: PolicyActivitySeriesQuery = {}) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.activity.series(policyId ?? '', qs),
    queryFn: () => policyApi.activitySeries(policyId as string, query),
    select: (series) => ({ ...series, points: Array.isArray(series.points) ? series.points : [] }),
    enabled: scoped && !!policyId,
    placeholderData: keepPreviousData,
  })
}

/** GET /policies/library/:id/activity/log · policyKeys.activity.log(id, qs) — this policy's admin actions (the audit store). */
export function usePolicyActivityLog(policyId: string | null | undefined, query: PolicyActivityLogQuery = {}) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.activity.log(policyId ?? '', qs),
    queryFn: () => policyApi.activityLog(policyId as string, query),
    select: (page: PolicyActivityLogPage) => normalisePage<PolicyActivityLogPage['data'][number], PolicyActivityLogPage>(page, query.limit ?? 25),
    placeholderData: keepPreviousData,
    enabled: scoped && !!policyId,
  })
}

/** GET /policies/dashboard?department= · policyKeys.dashboard(department) — where the organisation stands. */
export function usePolicyDashboard(department: string | null = null) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.dashboard(department),
    queryFn: () => policyApi.dashboard(department),
    select: (dash) => ({
      ...dash,
      dueIn14Days: { ...dash.dueIn14Days, policies: Array.isArray(dash.dueIn14Days?.policies) ? dash.dueIn14Days.policies : [] },
      lateJoiners: { ...dash.lateJoiners, sampleNames: Array.isArray(dash.lateJoiners?.sampleNames) ? dash.lateJoiners.sampleNames : [] },
      heatmap: {
        departments: Array.isArray(dash.heatmap?.departments) ? dash.heatmap.departments : [],
        rows: Array.isArray(dash.heatmap?.rows) ? dash.heatmap.rows.map((r) => ({ ...r, cells: Array.isArray(r.cells) ? r.cells : [] })) : [],
      },
      needsHelpQueue: Array.isArray(dash.needsHelpQueue) ? dash.needsHelpQueue : [],
      actFirst: Array.isArray(dash.actFirst) ? dash.actFirst : [],
      recentActivity: Array.isArray(dash.recentActivity) ? dash.recentActivity : [],
    }),
    enabled: scoped,
    placeholderData: keepPreviousData,
  })
}

/** GET …/versions/:versionId/ai/suggestions · policyKeys.ai.suggestions(id, versionId, qs) — what the draft's AI runs proposed. */
export function usePolicyAiSuggestions(
  policyId: string | null | undefined,
  versionId: string | null | undefined,
  query: PolicyAiSuggestionsQuery = {},
  enabled = true,
) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.ai.suggestions(policyId ?? '', versionId ?? '', qs),
    queryFn: () => policyApi.aiSuggestions(policyId as string, versionId as string, query),
    select: (rows: PolicyAiSuggestion[]) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled && !!policyId && !!versionId,
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// Writes — library and drafts
// ═════════════════════════════════════════════════════════════════════════════

/** POST /policies/library {} — an untitled 1.0 draft; open the editor with the returned ids. */
export function useCreatePolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => policyApi.createPolicy(),
    onSuccess: () => void invalidateLibrary(qc),
  })
}

/** POST /policies/library/:id/clone — "Duplicate": a Draft "… (copy)" with the file and items copied. */
export function useClonePolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId }: { policyId: string }) => policyApi.clonePolicy(policyId),
    onSuccess: () => void invalidateLibrary(qc),
  })
}

/** PATCH /policies/library/:id {title, coverKey} — the policy header (the title only while it is a Draft). */
export function useUpdatePolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: UpdatePolicyBody }) => policyApi.updatePolicy(policyId, body),
    onSuccess: (detail, { policyId }) => {
      qc.setQueryData(policyKeys.library.detail(policyId), detail)
      void invalidateLibrary(qc)
    },
  })
}

/** DELETE /policies/library/:id — a never-published policy only (`policy_cannot_delete_published`). */
export function useDeletePolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId }: { policyId: string }) => policyApi.deletePolicy(policyId),
    onSuccess: (_d, { policyId }) => {
      qc.removeQueries({ predicate: (q) => isPolicyKey(q.queryKey, policyId) })
      void invalidateLibrary(qc)
    },
  })
}

/**
 * POST /policies/library/:id/archive {reason, note?, replacedByPolicyId?} — approvers only; discards an unsubmitted version
 * draft (v3, L4: the reason is required; a later New version brings the policy back through approval).
 */
export function useArchivePolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: ArchivePolicyBody }) =>
      fetchApi<PolicyArchiveResult>(`${policyPaths.policy(policyId)}/archive`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_d, { policyId }) => {
      void invalidatePolicy(qc, policyId)
      void invalidateLibrary(qc)
      invalidateOrganisation(qc)
    },
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// Writes — versions, file, approval
// ═════════════════════════════════════════════════════════════════════════════

/**
 * POST /policies/library/:id/versions {changeType?} — "Update" (copies the current version, no file). v3 (E1): absent or
 * 'Material' = a New version, 'Minor' = a Revision (same number, revision + 1). `policy_draft_exists` carries {versionId}.
 */
export function useCreatePolicyVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, changeType }: { policyId: string; changeType?: PolicyChosenChangeType }) =>
      fetchApi<PolicyVersionCreated>(policyPaths.versions(policyId), { method: 'POST', body: JSON.stringify(changeType ? { changeType } : {}) }),
    onSuccess: (_d, { policyId }) => {
      void invalidatePolicy(qc, policyId)
      void invalidateLibrary(qc)
    },
  })
}

/** Does this autosave accept an AI suggestion? (Accepting is the item PATCH carrying `aiSuggestionId`.) */
function acceptsAiSuggestion(body: UpdatePolicyVersionBody): boolean {
  return (body.keyPoints ?? []).some((k) => !!k.aiSuggestionId) || (body.questions ?? []).some((q) => !!q.aiSuggestionId)
}

/**
 * PATCH …/versions/:versionId — the editor's autosave (the caller debounces ≈ 800 ms). The answer
 * IS the version, with its issues: it is written to the cache, so nothing refetches per keystroke.
 *
 * What else it moves, with the 5-minute default `staleTime` in mind (an entry nothing marks stale
 * keeps showing what it showed before the edit):
 *  - the library lists, this policy's page (`library.detail`: "Open v1.1 draft" reads the draft's
 *    label) and the Versions tab (`versions.list`) are marked stale WITHOUT a refetch
 *    (`refetchType: 'none'`), so they are read again the next time a screen shows them and nothing
 *    flickers per keystroke;
 *  - a PATCH that carries an `aiSuggestionId` has just ACCEPTED that suggestion on the server, so the
 *    suggestion lists are invalidated AND refetched where they are on screen (the default
 *    `refetchType`): otherwise the card keeps its Add button and a second click adds a duplicate
 *    item or is refused with 409 `policy_ai_suggestion_decided`. A refused label
 *    (`policy_version_number_invalid` / `_not_higher`) writes nothing and moves nothing here.
 */
export function useUpdatePolicyVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId, body }: { policyId: string; versionId: string; body: UpdatePolicyVersionBody }) =>
      policyApi.updateVersion(policyId, versionId, body),
    onSuccess: (version, { policyId, versionId, body }) => {
      qc.setQueryData(policyKeys.versions.detail(policyId, versionId), version)
      qc.setQueryData(policyKeys.versions.issues(policyId, versionId), { issues: version.issues })
      void qc.invalidateQueries({ queryKey: policyKeys.library.lists(), refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: policyKeys.library.detail(policyId), refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: policyKeys.versions.list(policyId), refetchType: 'none' })
      if (acceptsAiSuggestion(body)) void qc.invalidateQueries({ queryKey: policyKeys.ai.all(policyId) })
    },
  })
}

/** DELETE …/versions/:versionId — a Draft or Changes Requested version (a never-published policy's only version deletes the policy too). */
export function useDeletePolicyVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId }: { policyId: string; versionId: string }) => policyApi.deleteVersion(policyId, versionId),
    onSuccess: (_d, { policyId }) => {
      void invalidatePolicy(qc, policyId)
      void invalidateLibrary(qc)
      invalidateOrganisation(qc)
    },
  })
}

/** PUT …/versions/:versionId/file (multipart) — upload or replace the PDF. A refusal keeps the previous file. */
export function useUploadPolicyFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId, file }: { policyId: string; versionId: string; file: File }) =>
      uploadPolicyFile(policyId, versionId, file),
    onSuccess: ({ version }, { policyId, versionId }) => {
      qc.setQueryData(policyKeys.versions.detail(policyId, versionId), version)
      qc.setQueryData(policyKeys.versions.issues(policyId, versionId), { issues: version.issues })
      void invalidateVersionDerived(qc, policyId, versionId)
      void qc.invalidateQueries({ queryKey: policyKeys.library.lists(), refetchType: 'none' })
    },
  })
}

/**
 * POST …/versions/:versionId/file/restore — bring back the file "Replace File" put aside. The Drafts row
 * shows the file's page count and whether it has one, so the lists are marked stale (no refetch) like an upload does.
 */
export function useRestorePolicyFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId }: { policyId: string; versionId: string }) => policyApi.restoreFile(policyId, versionId),
    onSuccess: (version, { policyId, versionId }) => {
      qc.setQueryData(policyKeys.versions.detail(policyId, versionId), version)
      qc.setQueryData(policyKeys.versions.issues(policyId, versionId), { issues: version.issues })
      void invalidateVersionDerived(qc, policyId, versionId)
      void qc.invalidateQueries({ queryKey: policyKeys.library.lists(), refetchType: 'none' })
    },
  })
}

function useApprovalRefresh() {
  const qc = useQueryClient()
  return (policyId: string) => {
    void invalidatePolicy(qc, policyId)
    void invalidateLibrary(qc)
    invalidateOrganisation(qc)
  }
}

/** POST …/versions/:versionId/submit {approverUserId, note?} — locks the draft. `policy_submit_issues` names the first issue. */
export function useSubmitPolicyVersion() {
  const refresh = useApprovalRefresh()
  return useMutation({
    mutationFn: ({ policyId, versionId, body }: { policyId: string; versionId: string; body: SubmitPolicyVersionBody }) =>
      policyApi.submitVersion(policyId, versionId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/versions/:versionId/withdraw — the submitter only, while it waits; reopens the editor. */
export function useWithdrawPolicyVersion() {
  const refresh = useApprovalRefresh()
  return useMutation({
    mutationFn: ({ policyId, versionId }: { policyId: string; versionId: string }) => policyApi.withdrawVersion(policyId, versionId),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/versions/:versionId/request-changes {comment 5-500} — back to the submitter's drafts. Never the submitter's own. */
export function useRequestPolicyChanges() {
  const refresh = useApprovalRefresh()
  return useMutation({
    mutationFn: ({ policyId, versionId, body }: { policyId: string; versionId: string; body: RequestPolicyChangesBody }) =>
      policyApi.requestChanges(policyId, versionId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/versions/:versionId/approve {} — publishes (a material version opens a round and asks everyone again). Never the submitter's own. */
export function useApprovePolicyVersion() {
  const refresh = useApprovalRefresh()
  return useMutation({
    mutationFn: ({ policyId, versionId }: { policyId: string; versionId: string }) => policyApi.approveVersion(policyId, versionId),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// Writes — AI hooks (one paid run per file feeds the rest; nothing is added until the maker accepts)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * POST …/versions/:versionId/ai/runs {kind, pages?} — spends AI credits. A refusal is an error
 * (`policy_ai_no_credits`, `policy_ai_not_set_up`, `policy_ai_disabled`). A run that found no clear rules
 * is NOT one: it resolves with `run.status === 'No Result'` and `run.errorCode === 'policy_ai_no_rules'`,
 * which `policyAiRunNoticeKey(run)` (lib/policies/errors.ts) words. The suggestions come back with the run and are
 * cached under their list key; the shared AI allowance is refreshed so the credits pill is true.
 */
export function useRunPolicyAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId, body }: { policyId: string; versionId: string; body: RunPolicyAiBody }) =>
      policyApi.runAi(policyId, versionId, body),
    onSettled: (_d, _e, { policyId }) => {
      void qc.invalidateQueries({ queryKey: policyKeys.ai.all(policyId) })
      void qc.invalidateQueries({ queryKey: aiAllowanceKeys.all() })
    },
  })
}

/** POST /policies/ai/suggestions/:suggestionId/dismiss. Accepting is the item PATCH carrying `aiSuggestionId` (useUpdatePolicyVersion). */
export function useDismissPolicyAiSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ suggestionId }: { policyId: string; versionId: string; suggestionId: string }) => policyApi.dismissAiSuggestion(suggestionId),
    onSuccess: (_d, { policyId }) => void qc.invalidateQueries({ queryKey: policyKeys.ai.all(policyId) }),
  })
}

/**
 * POST …/versions/:versionId/ai/details {} — "Fill with AI" on the editor's details step (v3, A1). AI reads the maker's
 * PDF and fills only the details that are still empty; the maker checks each one. The answer carries the version
 * afterwards, which goes to the cache as a saved edit would (so the issue list and the AI marks are true). A refused or
 * failed run may still have moved the allowance, so the credits pill is refreshed either way. Refusals
 * (`policy_ai_no_credits`, `policy_ai_not_set_up`, `policy_version_locked`, `policy_file_not_found`) are worded by the
 * caller from their errorCode.
 */
export function useFillPolicyDetails() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId }: { policyId: string; versionId: string }) =>
      fetchApi<PolicyAiDetailsResult>(`${policyPaths.version(policyId, versionId)}/ai/details`, sendJson('POST', {})),
    onSuccess: (result, { policyId, versionId }) => {
      const version = normaliseVersion(result.version)
      qc.setQueryData(policyKeys.versions.detail(policyId, versionId), version)
      qc.setQueryData(policyKeys.versions.issues(policyId, versionId), { issues: version.issues })
      void qc.invalidateQueries({ queryKey: policyKeys.library.lists(), refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: policyKeys.library.detail(policyId), refetchType: 'none' })
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: aiAllowanceKeys.all() }),
  })
}

// ═════════════════════════════════════════════════════════════════════════════
// Writes — the policy page: remind, extend, reset, remove, restore, due date, reminders, assign
// ═════════════════════════════════════════════════════════════════════════════

function usePolicyWriteRefresh() {
  const qc = useQueryClient()
  return (policyId: string) => {
    void invalidatePolicy(qc, policyId)
    void qc.invalidateQueries({ queryKey: policyKeys.library.lists() })
    void qc.invalidateQueries({ queryKey: policyKeys.dashboards() })
  }
}

/** POST /policies/library/:id/remind {attestationIds?} — absent = every remindable row; nobody is mailed twice in a day. */
export function useRemindPolicy() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body?: RemindPolicyBody }) => policyApi.remind(policyId, body ?? {}),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST /policies/library/:id/people/extend {attestationIds, days, emailPending} — from each person's own due date. */
export function useExtendPolicyDue() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: ExtendPolicyBody }) => policyApi.extend(policyId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST /policies/library/:id/people/:attestationId/reset-attempts — keeps reading and key points, mails the person. */
export function useResetPolicyAttempts() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, attestationId }: { policyId: string; attestationId: string }) => policyApi.resetAttempts(policyId, attestationId),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST /policies/library/:id/people/:attestationId/remove {reason, note?}. */
export function useRemovePolicyRecipient() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, attestationId, body }: { policyId: string; attestationId: string; body: RemovePolicyRecipientBody }) =>
      policyApi.removeRecipient(policyId, attestationId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST /policies/library/:id/people/:attestationId/restore. */
export function useRestorePolicyRecipient() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, attestationId }: { policyId: string; attestationId: string }) => policyApi.restoreRecipient(policyId, attestationId),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** PATCH /policies/library/:id/due-date {dueOn, emailPending} — only people who have not acknowledged and still sit on the policy's due date. */
export function useChangePolicyDueDate() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: ChangePolicyDueDateBody }) => policyApi.changeDueDate(policyId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** PATCH /policies/library/:id/reminders — the four switches, written to every current-round assignment. */
export function useUpdatePolicyReminders() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: UpdatePolicyRemindersBody }) => policyApi.updateReminders(policyId, body),
    onSuccess: (reminders, { policyId }) => {
      qc.setQueryData(policyKeys.reminders(policyId), reminders)
      void qc.invalidateQueries({ queryKey: policyKeys.assignments.all(policyId) })
    },
  })
}

/** POST /policies/library/:id/assignments {audience, due, reminders} — mails each new person now. A due date before the effective date only warns. */
export function useAssignPolicy() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: AssignPolicyBody }) => policyApi.assign(policyId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST /policies/library/:id/ask-again {dueDays} — assign the same version again as a new round. */
export function useAskPolicyAgain() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: AskPolicyAgainBody }) => policyApi.askAgain(policyId, body),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** GET /policies/evidence/verify — recompute the proof chain and every timeline. A button, not a poll (no UI in v1). */
export function useVerifyPolicyEvidence() {
  return useMutation({ mutationFn: () => policyApi.verifyEvidence() })
}

// ═════════════════════════════════════════════════════════════════════════════
// Downloads — each saves the file the API sends; failures are errorCodes like any other
// ═════════════════════════════════════════════════════════════════════════════

/** GET /policies/library/export.csv?section= — one tab as filtered, written in the interface language. */
export function useExportPolicyLibraryCsv() {
  const { locale } = useTranslation()
  return useMutation({
    mutationFn: ({ section, query }: { section: PolicyLibrarySection; query: PolicyLibraryQuery | PolicyDraftsQuery | PolicyArchivedQuery }) =>
      exportPolicyLibraryCsv(section, query, locale),
  })
}

/** GET /policies/library/:id/people/export.csv — recipients as filtered, removed people and leavers included. */
export function useExportPolicyPeopleCsv() {
  const { locale } = useTranslation()
  return useMutation({
    mutationFn: ({ policyId, query }: { policyId: string; query: PolicyPeopleQuery }) => exportPolicyPeopleCsv(policyId, query, locale),
  })
}

/** GET /policies/library/:id/people/:attestationId/evidence.pdf — the receipt plus the full timeline. */
export function useDownloadPolicyEvidencePdf() {
  const { locale } = useTranslation()
  return useMutation({
    mutationFn: ({ policyId, attestationId }: { policyId: string; attestationId: string }) => downloadPolicyEvidencePdf(policyId, attestationId, locale),
  })
}

/**
 * GET /policies/evidence/:evidenceId/receipt.pdf — the person's receipt, from the admin side. `evidenceId` is the
 * ACKNOWLEDGMENT's uuid (`PortalPolicyReceipt.evidenceId`, i.e. `record.acknowledgment.evidenceId` of a person's evidence
 * record, null until they acknowledge), never the `EV-…` text that `evidenceRef` / `reference` hold: that one is 404
 * `policy_evidence_not_found`.
 */
export function useDownloadPolicyReceiptPdf() {
  const { locale } = useTranslation()
  return useMutation({
    mutationFn: ({ evidenceId }: { evidenceId: string }) => downloadPolicyReceiptPdf(evidenceId, locale),
  })
}

/** GET …/versions/:versionId/file?download=1 — the stored PDF, saved under its own name. */
export function useDownloadPolicyVersionFile() {
  return useMutation({
    mutationFn: async ({ policyId, versionId }: { policyId: string; versionId: string }) => {
      savePolicyBlob(await fetchPolicyVersionFile(policyId, versionId, true))
    },
  })
}

/**
 * The PDF renderer's loader (components/policies/pdf): a stable function that reads the bytes
 * of whichever source it is handed — an admin's version, a portal task, a signed-link task —
 * with the auth that source needs. Not a query: the renderer keeps the parsed document in its
 * own cache, keyed by the file's SHA-256.
 */
export function usePolicyFileLoader(): (source: PolicyPdfSource, signal?: AbortSignal) => Promise<PolicyPdfBytes> {
  return useCallback((source: PolicyPdfSource, signal?: AbortSignal) => fetchPolicyPdfBytes(source, signal), [])
}

// ═════════════════════════════════════════════════════════════════════════════
// v3 (W1, 2026-10-16): the document-first policy page, its dialogs, the review page and the as-of page
// ═════════════════════════════════════════════════════════════════════════════

const sendJson = (method: 'POST' | 'PATCH', body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })
const atPath = (path: string, qs: string) => (qs ? `${path}?${qs}` : path)
const EXEMPTIONS_PATH = '/policies/exemptions'

/** GET /policies/library/:id/reviews · policyKeys.reviews(id) — "Mark as reviewed" history, newest first (L5). */
export function usePolicyReviews(policyId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.reviews(policyId ?? ''),
    queryFn: () => fetchApi<PolicyReviewRow[]>(`${policyPaths.policy(policyId as string)}/reviews`),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled && !!policyId,
  })
}

/**
 * GET /policies/library/:id/as-of?day=… · policyKeys.asOf(id, qs) — the policy as it stood at the end of a past workspace
 * day (F4). Pass null to hold the request. A day before the first publish or after today is 400 `policy_as_of_invalid`
 * {earliest, today}: an answer, not a hiccup (no retry).
 */
export function usePolicyAsOf(policyId: string | null | undefined, query: PolicyAsOfQuery | null) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query ?? {})
  return useQuery({
    queryKey: policyKeys.asOf(policyId ?? '', qs),
    queryFn: () => fetchApi<PolicyAsOf>(atPath(`${policyPaths.policy(policyId as string)}/as-of`, qs)),
    select: (page: PolicyAsOf) => normalisePage<PolicyAsOf['data'][number], PolicyAsOf>(page, query?.limit ?? 40),
    placeholderData: keepPreviousData,
    enabled: scoped && !!policyId && !!query,
    retry: (count, err) => !(err instanceof ApiClientError && err.statusCode >= 400 && err.statusCode < 500) && count < 2,
  })
}

/** GET /policies/exemptions?… · policyKeys.exemptions.list(qs) — requests and exemptions (E3); `policyId` narrows to one policy (and All Policies). */
export function usePolicyExemptions(query: PolicyExemptionsQuery = {}, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  const qs = qsOf(query)
  return useQuery({
    queryKey: policyKeys.exemptions.list(qs),
    queryFn: () => fetchApi<PolicyExemptionPage>(atPath(EXEMPTIONS_PATH, qs)),
    select: (page: PolicyExemptionPage): PolicyExemptionPage => {
      const base = normalisePage<PolicyExemptionPage['data'][number], PolicyExemptionPage>(page, query.limit ?? 25)
      return { ...base, statusCounts: { ...zeroCounts(POLICY_EXEMPTION_STATUSES), ...(page?.statusCounts ?? {}) } }
    },
    placeholderData: keepPreviousData,
    enabled: scoped && enabled,
  })
}

/** GET /policies/evidence/integrity · policyKeys.integrity() — the latest nightly chain check (L9). */
export function usePolicyEvidenceIntegrity(enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.integrity(),
    queryFn: () => fetchApi<PolicyEvidenceIntegrity>('/policies/evidence/integrity'),
    enabled: scoped && enabled,
    staleTime: 60_000,
    retry: false,
  })
}

/** GET …/rounds/:round/compliance-preview · policyKeys.compliancePreview(id, round) — what "Make it count" will change (D3). */
export function usePolicyCompliancePreview(policyId: string | null | undefined, round: number | null, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.compliancePreview(policyId ?? '', round ?? 0),
    queryFn: () => fetchApi<PolicyCompliancePreview>(`${policyPaths.policy(policyId as string)}/rounds/${round}/compliance-preview`),
    enabled: scoped && enabled && !!policyId && round !== null,
    gcTime: 0,
    retry: false,
  })
}

/**
 * POST …/ask-again/preview · policyKeys.askAgainPreview(id, json) — the dialog's head-count for the chosen `who` and
 * days (E2). A read that happens to be a POST: it sends nothing. Pass null to hold the request.
 */
export function usePolicyAskAgainPreview(policyId: string | null | undefined, body: PreviewAskPolicyAgainBody | null) {
  const scoped = useWorkspaceScopedEnabled()
  const key = body ? JSON.stringify(body) : ''
  return useQuery({
    queryKey: policyKeys.askAgainPreview(policyId ?? '', key),
    queryFn: () => fetchApi<PolicyAskAgainPreview>(`${policyPaths.policy(policyId as string)}/ask-again/preview`, sendJson('POST', body)),
    enabled: scoped && !!policyId && !!body,
    placeholderData: keepPreviousData,
    gcTime: 0,
    retry: false,
  })
}

/** PATCH …/details {ownerUserId?, category?, reviewBy?, controlTags?} — metadata outside the signed content, audited old → new (L11). */
export function useUpdatePolicyDetails() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: UpdatePolicyDetailsBody }) =>
      fetchApi<PolicyDetailsChanged>(`${policyPaths.policy(policyId)}/details`, sendJson('PATCH', body)),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/reviews {nextReviewOn, note?} — "Mark as reviewed" (L5): records that the owner checked the policy; asks nobody. */
export function useMarkPolicyReviewed() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: MarkPolicyReviewedBody }) =>
      fetchApi<PolicyReviewRow>(`${policyPaths.policy(policyId)}/reviews`, sendJson('POST', body)),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/rounds/:round/count-toward-compliance — one-way (D3): an acknowledgment-only round starts to count. Approvers only. */
export function useCountTowardCompliance() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, round }: { policyId: string; round: number }) =>
      fetchApi<PolicyComplianceChangeResult>(`${policyPaths.policy(policyId)}/rounds/${round}/count-toward-compliance`, sendJson('POST', {})),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/assignments/:assignmentId/send-now — release a waiting assignment at once (D1). */
export function useSendAssignmentNow() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, assignmentId }: { policyId: string; assignmentId: string }) =>
      fetchApi<PolicySendNowResult>(`${policyPaths.policy(policyId)}/assignments/${encodeURIComponent(assignmentId)}/send-now`, sendJson('POST', {})),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** POST …/assignments/:assignmentId/cancel — nobody in a waiting assignment is assigned (D1). */
export function useCancelAssignment() {
  const refresh = usePolicyWriteRefresh()
  return useMutation({
    mutationFn: ({ policyId, assignmentId }: { policyId: string; assignmentId: string }) =>
      fetchApi<PolicyAssignmentCancelResult>(`${policyPaths.policy(policyId)}/assignments/${encodeURIComponent(assignmentId)}/cancel`, sendJson('POST', {})),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

function useExemptionRefresh() {
  const qc = useQueryClient()
  const refresh = usePolicyWriteRefresh()
  return (policyId: string | null) => {
    void qc.invalidateQueries({ queryKey: policyKeys.exemptions.all() })
    // The inbox (`exemptionsToDecide`), the sidebar badge and the "waiting for you" counts all count requests.
    void invalidateLibrary(qc)
    invalidateOrganisation(qc)
    if (policyId) refresh(policyId)
    else void qc.invalidateQueries({ queryKey: policyKeys.all() })
  }
}

/** POST /policies/exemptions {recipientIds, policyId|null, note, endsOn} — one request per person (E3). `policyId` null = All Policies. */
export function useRequestPolicyExemptions() {
  const refresh = useExemptionRefresh()
  return useMutation({
    mutationFn: ({ body }: { body: RequestPolicyExemptionsBody }) =>
      fetchApi<PolicyExemptionRequestResult>(EXEMPTIONS_PATH, sendJson('POST', body)),
    onSuccess: (_d, { body }) => refresh(body.policyId),
  })
}

/**
 * POST /policies/exemptions/:id/approve | decline | withdraw | end (E3, D10). `approve` and `end` take an optional note,
 * `decline` a required one, `withdraw` none. The exempted person never decides their own (403 `policy_self_approval`
 * {reason: own_exemption}).
 */
export function useDecidePolicyExemption() {
  const refresh = useExemptionRefresh()
  return useMutation({
    mutationFn: ({
      exemptionId,
      action,
      body,
    }: {
      exemptionId: string
      /** The policy the exemption belongs to (null = All Policies): what the page refreshes. */
      policyId: string | null
      action: 'approve' | 'decline' | 'withdraw' | 'end'
      body?: DecidePolicyExemptionBody | DeclinePolicyExemptionBody
    }) => fetchApi<PolicyExemptionDecisionResult>(`${EXEMPTIONS_PATH}/${encodeURIComponent(exemptionId)}/${action}`, sendJson('POST', body ?? {})),
    onSuccess: (_d, { policyId }) => refresh(policyId),
  })
}

/** GET …/versions/:versionId/tests · policyKeys.practice.tests(id, versionId) — the caller's own test sends, newest first (L16). */
export function usePolicyTestRuns(policyId: string | null | undefined, versionId: string | null | undefined, enabled = true) {
  const scoped = useWorkspaceScopedEnabled()
  return useQuery({
    queryKey: policyKeys.practice.tests(policyId ?? '', versionId ?? ''),
    queryFn: () => fetchApi<PolicyTestRunRow[]>(`${policyPaths.version(policyId as string, versionId as string)}/tests`),
    select: (rows) => (Array.isArray(rows) ? rows : []),
    enabled: scoped && enabled && !!policyId && !!versionId,
  })
}

/** POST …/versions/:versionId/tests {…} — "Send a test to myself" (C2, L16): the caller's own address only; nothing is recorded as a figure. */
export function useCreatePolicyTestRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, versionId, body }: { policyId: string; versionId: string; body: CreatePolicyTestRunBody }) =>
      fetchApi<PolicyTestRunRow>(`${policyPaths.version(policyId, versionId)}/tests`, sendJson('POST', body)),
    onSuccess: (_d, { policyId, versionId }) => void qc.invalidateQueries({ queryKey: policyKeys.practice.tests(policyId, versionId) }),
  })
}
